import React, { useState, useContext, useEffect, useRef } from "react";
import { GlobalContext } from "../context/GlobalContext";
import { Input } from "../components/Input";
import { PrimaryButtonSmall, SecondaryButtonSmall } from "../components/Button";
import { showToast } from "../utils/showToast";
import { ArrowUturnLeftIcon, TrashIcon, PencilSquareIcon, SparklesIcon, InformationCircleIcon, Cog6ToothIcon, ChevronDownIcon } from "@heroicons/react/20/solid";
import EventAPI from "../api/EventApi";
import AITextExtractor from "../components/AITextExtractor";
import { sanitizeEmailHtml } from "../utils/sanitizeHtml";

export const EmailSection = ({ eventID, eventData }) => {
  const { globalState } = useContext(GlobalContext);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [to, setTo] = useState("");
  const [loading, setLoading] = useState(false);
  const [emails, setEmails] = useState([]);
  const [showCompose, setShowCompose] = useState(false);
  const [confirmVenue, setConfirmVenue] = useState(false);
  const [venueConfirmLoading, setVenueConfirmLoading] = useState(false);
  const [inboxExpanded, setInboxExpanded] = useState(false);

  // AI Feature States
  const [showAIMenu, setShowAIMenu] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [generatedContent, setGeneratedContent] = useState(null);
  const [showAIAgentPrompt, setShowAIAgentPrompt] = useState(false);
  const [aiAgentPrompt, setAiAgentPrompt] = useState("");
  const [showAgentDetails, setShowAgentDetails] = useState(false);
  const [selectedEmailForDetails, setSelectedEmailForDetails] = useState(null);
  const aiMenuRef = useRef(null);

  useEffect(() => {
    if (!eventID) return;

    const unsubscribe = EventAPI.subscribeToEmails(eventID, (emailList) => {
      setEmails(emailList);
    });

    return () => unsubscribe();
  }, [eventID]);

  useEffect(() => {
    if (eventData) {
      setConfirmVenue(eventData.isVenueConfirmed || false);
    }
  }, [eventData]);

  const handleSendEmail = async () => {
    if (!to || !subject || !body) {
      showToast("Please fill in all fields", { type: "error" });
      return;
    }

    setLoading(true);
    try {
      const result = await EventAPI.sendEmail(
        to,
        subject,
        body,
        eventID,
        globalState?.user?.name || "User"
      );

      if (result.success) {
        showToast("Email sent successfully!", { type: "success" });
        setSubject("");
        setBody("");
        setTo("");
        setShowCompose(false);
      } else {
        showToast("Failed to send email", { type: "error" });
      }
    } catch (error) {
      console.error("Error sending email:", error);
      showToast(error.message || "Error sending email", { type: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleReply = (email) => {
    const replyTo = email.direction === "received" ? email.from : (Array.isArray(email.to) ? email.to[0] : email.to);
    setTo(replyTo);

    // Prefix subject with Re: if not already there
    const currentSubject = email.subject || "";
    const newSubject = /^re:/i.test(currentSubject) ? currentSubject : `Re: ${currentSubject}`;
    setSubject(newSubject);

    // Format original message
    const dateStr = email.sentAt?.toDate ? email.sentAt.toDate().toLocaleString() : "unknown date";
    const fromStr = email.from || "unknown sender";

    // Get plain text body (fallback to stripped HTML)
    let originalBody = email.text || "";
    if (!originalBody && email.html) {
      originalBody = email.html
        .replace(/<br\s*\/?>/gi, '\n')
        .replace(/<p>/gi, '')
        .replace(/<\/p>/gi, '\n')
        .replace(/<[^>]*>?/gm, '');
    }

    const quotedBody = originalBody.split('\n').map(line => `> ${line}`).join('\n');
    const replyBody = `\n\nOn ${dateStr}, ${fromStr} wrote:\n${quotedBody}`;

    setBody(replyBody);
    setShowCompose(true);
  };

  const handleDeleteEmail = async (emailID) => {
    if (window.confirm("Are you sure you want to delete this email?")) {
      try {
        await EventAPI.deleteEmail(eventID, emailID);
        showToast("Email deleted successfully", { type: "success" });
      } catch (error) {
        console.error("Error deleting email:", error);
        showToast("Failed to delete email", { type: "error" });
      }
    }
  };

  const handleAIAgent = async () => {
    setShowAIMenu(false);
    try {
      const existingPrompt = await EventAPI.getAIAgentPrompt(eventID);
      setAiAgentPrompt(existingPrompt || "");
    } catch (error) {
      console.error("Error loading AI Agent prompt:", error);
      setAiAgentPrompt("");
    }
    setShowAIAgentPrompt(true);
  };

  const handleGenerateAIAgentReply = async () => {
    if (!aiAgentPrompt.trim()) {
      showToast("Please provide instructions for the AI Agent", { type: "error" });
      return;
    }

    setAiLoading(true);
    try {
      await EventAPI.saveAIAgentPrompt(eventID, aiAgentPrompt);

      setShowAIAgentPrompt(false);
      showToast("AI Agent prompt saved successfully", { type: "success" });
    } catch (error) {
      console.error("Error saving AI Agent prompt:", error);
      showToast(error.message || "Error saving prompt", { type: "error" });
    } finally {
      setAiLoading(false);
    }
  };

  const handleDeletePrompt = async () => {
    setAiLoading(true);
    try {
      setAiAgentPrompt("");
      await EventAPI.saveAIAgentPrompt(eventID, "");
      showToast("AI Agent prompt deleted successfully", { type: "success" });
    } catch (error) {
      console.error("Error deleting AI Agent prompt:", error);
      showToast(error.message || "Error deleting prompt", { type: "error" });
    } finally {
      setAiLoading(false);
    }
  };

  const handleCloseAIAgentPrompt = () => {
    setShowAIAgentPrompt(false);
    setAiAgentPrompt("");
  };

  const handleToggleVenueConfirm = async () => {
    const newValue = !confirmVenue;
    setVenueConfirmLoading(true);
    try {
      await EventAPI.updateEvent(eventID, { isVenueConfirmed: newValue });
      setConfirmVenue(newValue);
      showToast(`Venue confirmation ${newValue ? 'enabled' : 'disabled'}`, { type: "success" });
    } catch (error) {
      console.error("Error updating venue confirmation:", error);
      showToast("Failed to update venue confirmation", { type: "error" });
    } finally {
      setVenueConfirmLoading(false);
    }
  };

  const handleAcceptGenerated = () => {
    if (generatedContent) {
      setTo(generatedContent.to || "");
      setSubject(generatedContent.subject || "");
      setBody(generatedContent.body || "");
      // setGeneratedContent(null);
      setShowCompose(true);
      showToast("Email content loaded. You can now edit and send.", { type: "success" });
    }
  };

  return (
    <div className="space-y-4">
      {confirmVenue && (
        <div className="bg-green-50 dark:bg-green-900/40 border border-green-200 dark:border-green-800 rounded-md p-3">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-green-400 dark:text-green-300" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-green-800 dark:text-green-300">The venue has been confirmed</p>
            </div>
          </div>
        </div>
      )}
      <div className="px-0 sm:p-4">
        <div className={`flex justify-between items-center mb-4 ${confirmVenue ? 'cursor-pointer bg-gray-50 dark:bg-gray-800 p-2 rounded' : ''}`} onClick={confirmVenue ? () => setInboxExpanded(!inboxExpanded) : undefined}>
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-medium text-gray-700 dark:text-gray-200">Inbox</h3>
            {confirmVenue && (
              <ChevronDownIcon className={`h-5 w-5 text-gray-500 dark:text-gray-400 transition-transform ${inboxExpanded ? 'rotate-180' : ''}`} />
            )}
          </div>
          <div className="flex gap-2 items-center relative">
            {/* AI Button with Dropdown */}
            <div className="relative" ref={aiMenuRef}>
              <button
                onClick={(e) => { e.stopPropagation(); setShowAIMenu(!showAIMenu); }}
                disabled={aiLoading}
                className="flex items-center gap-2 text-sm text-primary border border-primary py-2 px-4 rounded  disabled:opacity-50"
              >
                <Cog6ToothIcon className="h-4 w-4" />
              </button>

              {/* AI Dropdown Menu */}
              {showAIMenu && (
                <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded shadow-lg z-40">
                  <button
                    onClick={handleAIAgent}
                    disabled={aiLoading}
                    className="w-full text-left px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <div className="font-medium text-gray-800 dark:text-gray-200">Agent</div>
                    <div className="text-xs text-gray-600 dark:text-gray-300">Auto-reply to email</div>
                  </button>
                  <button
                    onClick={handleToggleVenueConfirm}
                    disabled={venueConfirmLoading}
                    className="w-full text-left px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 border-t border-gray-200 dark:border-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <div className="flex justify-between items-center">
                      <div>
                        <div className="font-medium text-gray-800 dark:text-gray-200">Confirm</div>
                        <div className="text-xs text-gray-600 dark:text-gray-300">Venue confirmation</div>
                      </div>
                      <div className={`w-4 h-4 rounded ${confirmVenue ? 'bg-primary' : 'bg-gray-300 dark:bg-gray-600'} ${venueConfirmLoading ? 'animate-pulse' : ''}`}></div>
                    </div>
                  </button>
                </div>
              )}
            </div>

            {/* Compose Button */}
            <PrimaryButtonSmall onClick={() => setShowCompose(true)} className="flex items-center gap-2 text-sm">
              <PencilSquareIcon className="h-4 w-4" />
              Compose
            </PrimaryButtonSmall>
          </div>
        </div>
        {(!confirmVenue || inboxExpanded) && (
          <>
            <div className="mb-6">
              <AITextExtractor
                type="email"
                eventID={eventID}
                eventData={eventData}
                userName={globalState?.user?.name || "User"}
                onExtractedData={(data) => setGeneratedContent(data)}
                onAccept={handleAcceptGenerated}
                initialCollapsed={emails.length > 0}
              />
            </div>

            {emails.length > 0 && (
              <div className="space-y-4">
                {emails.map((email) => {
                  const isReceived = email.direction === "received";
                  const badgeColor = isReceived
                    ? "bg-primary text-white border-primary"
                    : "bg-stone-400 text-white border-stone-00";

                  return (
                    <div
                      key={email.id}
                      className="bg-stone-50 dark:bg-gray-800 p-4 rounded border border-stone-300 dark:border-gray-600 shadow-sm"
                    >
                      <div className="flex justify-between items-center mb-2 pb-2 border-b border-gray-200 dark:border-gray-700">
                        <SecondaryButtonSmall
                          onClick={() => handleReply(email)}
                          className="!py-1 !px-3 text-[10px] flex items-center gap-1"
                        >
                          <ArrowUturnLeftIcon className="h-3 w-3" />
                          REPLY
                        </SecondaryButtonSmall>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <span className={`px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider rounded border flex items-center gap-1 ${badgeColor}`}>
                            {email.direction || "sent"}
                            {email.aiReply && <SparklesIcon className="h-3 w-3" />}
                          </span>
                          {email.opened && (
                            <span className="px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider rounded border flex items-center gap-1 bg-blue-100 text-blue-700 border-blue-300 dark:bg-blue-900/40 dark:text-blue-300 dark:border-blue-800">
                              Opened
                            </span>
                          )}
                          {email.bounced && (
                            <span className="px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider rounded border flex items-center gap-1 bg-red-100 text-red-700 border-red-300 dark:bg-red-900/40 dark:text-red-300 dark:border-red-800">
                              Bounced
                            </span>
                          )}
                          {email.suppressed && (
                            <span className="px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider rounded border flex items-center gap-1 bg-orange-100 text-orange-700 border-orange-300 dark:bg-orange-900/40 dark:text-orange-300 dark:border-orange-800">
                              Suppressed
                            </span>
                          )}
                          {email.failed && (
                            <span className="px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider rounded border flex items-center gap-1 bg-red-100 text-red-700 border-red-300 dark:bg-red-900/40 dark:text-red-300 dark:border-red-800" title={email.failedReason || ''}>
                              Failed
                            </span>
                          )}
                          {email.aiAgentReasoning && (
                            <button
                              onClick={() => {
                                setSelectedEmailForDetails(email);
                                setShowAgentDetails(true);
                              }}
                              className="text-primary"
                            >
                              <InformationCircleIcon className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                        <button
                          onClick={() => handleDeleteEmail(email.id)}
                          className="text-red-500 dark:text-red-400 hover:text-red-700"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>

                      </div>
                      <div className="flex flex-col gap-1 mb-2">
                        {/* Row 1: Label and Date */}
                        <div className="flex justify-between items-center">
                          <span className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase">
                            {isReceived ? "From" : "To"}
                          </span>
                          <p className="text-[10px] text-gray-400 dark:text-gray-500 whitespace-nowrap">
                            {email.sentAt?.toDate
                              ? email.sentAt.toDate().toLocaleString()
                              : "Sending..."}
                          </p>
                        </div>
                        {/* Row 2: Email */}
                        <div className="flex justify-between items-center">
                          <p className="text-xs sm:text-sm font-bold text-gray-700 dark:text-gray-200 break-all">
                            {isReceived
                              ? email.from
                              : (Array.isArray(email.to) ? email.to.join(", ") : email.to)
                            }
                          </p>
                        </div>
                      </div>
                      <p className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-1">
                        {email.subject}
                      </p>
                      <div
                        className="text-xs text-gray-600 dark:text-gray-300 mt-2 p-2 sm:p-3 bg-stone-200 dark:bg-gray-700 rounded border border-gray-100 dark:border-gray-800 max-h-72 overflow-y-auto"
                        dangerouslySetInnerHTML={{ __html: sanitizeEmailHtml(email.html || `<pre style="white-space: pre-wrap;">${email.text || ""}</pre>`) }}
                      />
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>

      {/* AI Agent Prompt Modal */}
      {showAIAgentPrompt && (
        <>
          {/* Small Screen - iOS Sheet */}
          <div style={{ margin: 0 }} className="sm:hidden fixed inset-0 bg-gray-600 bg-opacity-50 z-50 flex flex-col" onClick={handleCloseAIAgentPrompt}>
            <div className="flex-1" onClick={handleCloseAIAgentPrompt}></div>
            <div className="relative w-full shadow-lg rounded-t-3xl bg-white dark:bg-gray-800 flex flex-col max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
              {/* Handle Bar */}
              <div className="flex justify-center pt-3 pb-2">
                <div className="w-12 h-1 bg-gray-300 dark:bg-gray-600 rounded-full"></div>
              </div>

              <div className="flex justify-between items-center px-5 pb-2">
                <h3 className="text-lg font-medium text-gray-700 dark:text-gray-200">AI Agent Instructions</h3>
                <button onClick={handleCloseAIAgentPrompt} className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300">
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="space-y-4 px-5 pb-5 flex-1 overflow-y-auto">
                <div>
                  <label className="block text-sm font-light text-gray-500 dark:text-gray-400 mb-2">
                    How should the AI Agent respond to replies? When should it contact you?
                  </label>
                  <textarea
                    value={aiAgentPrompt}
                    onChange={(e) => setAiAgentPrompt(e.target.value)}
                    rows={5}
                    className="text-sm block w-full rounded-md border border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 shadow-sm focus:border-primary focus:ring-primary p-2"
                    placeholder="E.g., 'Be professional, provide event details and contact me if my attention is needed.'"
                  />
                </div>
                <div className="flex justify-between items-center gap-3 pb-5">
                  <button
                    onClick={handleDeletePrompt}
                    disabled={aiLoading}
                    className="p-2 text-red-500 dark:text-red-400 hover:text-red-700 disabled:opacity-50"
                    title="Delete Prompt"
                  >
                    <TrashIcon className="h-5 w-5" />
                  </button>
                  <div className="flex gap-3">
                    <button
                      onClick={handleCloseAIAgentPrompt}
                      className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 text-xs font-extralight rounded-md hover:bg-gray-300 dark:hover:bg-gray-600"
                    >
                      Cancel
                    </button>
                    <PrimaryButtonSmall className="text-xs" onClick={handleGenerateAIAgentReply} loading={aiLoading}>
                      {aiLoading ? "Saving..." : "Save Instructions"}
                    </PrimaryButtonSmall>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Large Screen - Centered Modal */}
          <div style={{ margin: 0 }} className="hidden sm:flex fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 items-center justify-center" onClick={handleCloseAIAgentPrompt}>
            <div className="relative mx-auto p-5 border dark:border-gray-700 max-w-lg w-full shadow-lg rounded-md bg-white dark:bg-gray-800" onClick={(e) => e.stopPropagation()}>
              <div className="flex justify-between items-center mb-2">
                <h3 className="text-lg font-medium text-gray-700 dark:text-gray-200">AI Agent Instructions</h3>
                <button onClick={handleCloseAIAgentPrompt} className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300">
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-light text-gray-500 dark:text-gray-400 mb-2">
                    How should the AI Agent respond to replies? When should it contact you?
                  </label>
                  <textarea
                    value={aiAgentPrompt}
                    onChange={(e) => setAiAgentPrompt(e.target.value)}
                    rows={5}
                    className="text-sm block w-full rounded-md border border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 shadow-sm focus:border-primary focus:ring-primary p-2"
                    placeholder="E.g., 'Be professional, provide event details and contact me if my attention is needed.'"
                  />
                </div>
                <div className="flex justify-between items-center gap-3">
                  <button
                    onClick={handleDeletePrompt}
                    disabled={aiLoading}
                    className="p-2 text-red-500 dark:text-red-400 hover:text-red-700 disabled:opacity-50"
                    title="Delete Prompt"
                  >
                    <TrashIcon className="h-5 w-5" />
                  </button>
                  <div className="flex gap-3">
                    <button
                      onClick={handleCloseAIAgentPrompt}
                      className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 text-xs font-extralight rounded-md hover:bg-gray-300 dark:hover:bg-gray-600"
                    >
                      Cancel
                    </button>
                    <PrimaryButtonSmall className="text-xs" onClick={handleGenerateAIAgentReply} loading={aiLoading}>
                      {aiLoading ? "Saving..." : "Save Instructions"}
                    </PrimaryButtonSmall>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}


      {showCompose && (
        <>
          {/* Small Screen - iOS Sheet */}
          <div style={{ margin: 0 }} className="sm:hidden fixed inset-0 bg-gray-600 bg-opacity-50 z-50 flex flex-col">
            <div className="flex-1"></div>
            <div className="relative w-full shadow-lg rounded-t-3xl bg-white dark:bg-gray-800 flex flex-col max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
              {/* Handle Bar */}
              <div className="flex justify-center pt-3 pb-2">
                <div className="w-12 h-1 bg-gray-300 dark:bg-gray-600 rounded-full"></div>
              </div>

              <div className="flex justify-between items-center px-5 pb-4 flex-shrink-0">
                <h3 className="text-lg font-medium text-gray-700 dark:text-gray-200">Compose Email</h3>
                <button onClick={() => setShowCompose(false)} className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300">
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="space-y-4 flex-grow flex flex-col overflow-y-auto px-5">
                <Input
                  label="To"
                  value={to}
                  onChange={(e) => setTo(e.target.value)}
                  placeholder="recipient@example.com"
                  required={false}
                />
                <Input
                  label="Subject"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="Enter email subject"
                  required={false}
                />
                <div className="flex-grow flex flex-col">
                  <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                    Body
                  </label>
                  <textarea
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                    className="text-sm block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 shadow-sm focus:border-primary focus:ring-primary sm:text-sm flex-grow min-h-[200px]"
                    placeholder="Write your email here..."
                  />
                </div>
                <div className="flex justify-end gap-3 flex-shrink-0 pb-5">
                  <button
                    onClick={() => setShowCompose(false)}
                    className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 text-sm font-medium rounded-md hover:bg-gray-300 dark:hover:bg-gray-600"
                  >
                    Cancel
                  </button>
                  <PrimaryButtonSmall onClick={handleSendEmail} loading={loading}>
                    {loading ? "Sending..." : "Send Email"}
                  </PrimaryButtonSmall>
                </div>
              </div>
            </div>
          </div>

          {/* Large Screen - Centered Modal */}
          <div style={{ margin: 0 }} className="hidden sm:flex fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 items-center justify-center">
            <div className="relative mx-auto p-5 border dark:border-gray-700 max-w-lg w-full shadow-lg rounded-md bg-white dark:bg-gray-800 flex flex-col">
              <div className="flex justify-between items-center mb-4 flex-shrink-0">
                <h3 className="text-lg font-medium text-gray-700 dark:text-gray-200">Compose Email</h3>
                <button onClick={() => setShowCompose(false)} className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300">
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="space-y-4 flex-grow flex flex-col overflow-y-auto">
                <Input
                  label="To"
                  value={to}
                  onChange={(e) => setTo(e.target.value)}
                  placeholder="recipient@example.com"
                  required={false}
                />
                <Input
                  label="Subject"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="Enter email subject"
                  required={false}
                />
                <div className="flex-grow flex flex-col">
                  <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                    Body
                  </label>
                  <textarea
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                    className="text-sm block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 shadow-sm focus:border-primary focus:ring-primary sm:text-sm flex-grow min-h-[200px]"
                    placeholder="Write your email here..."
                  />
                </div>
                <div className="flex justify-end gap-3 flex-shrink-0">
                  <button
                    onClick={() => setShowCompose(false)}
                    className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 text-sm font-medium rounded-md hover:bg-gray-300 dark:hover:bg-gray-600"
                  >
                    Cancel
                  </button>
                  <PrimaryButtonSmall onClick={handleSendEmail} loading={loading}>
                    {loading ? "Sending..." : "Send Email"}
                  </PrimaryButtonSmall>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Agent Details Modal */}
      {showAgentDetails && selectedEmailForDetails && (
        <>
          {/* Small Screen - iOS Sheet */}
          <div style={{ margin: 0 }} className="sm:hidden fixed inset-0 bg-gray-600 bg-opacity-50 z-50 flex flex-col" onClick={() => setShowAgentDetails(false)}>
            <div className="flex-1" onClick={() => setShowAgentDetails(false)}></div>
            <div className="relative w-full shadow-lg rounded-t-3xl bg-white dark:bg-gray-800 flex flex-col max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
              {/* Handle Bar */}
              <div className="flex justify-center pt-3 pb-2">
                <div className="w-12 h-1 bg-gray-300 dark:bg-gray-600 rounded-full"></div>
              </div>

              <div className="flex justify-between items-center px-5 pb-4 flex-shrink-0">
                <h3 className="text-lg font-medium text-gray-700 dark:text-gray-200">AI Agent Details</h3>
                <button onClick={() => setShowAgentDetails(false)} className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300">
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="space-y-4 flex-grow overflow-y-auto px-5 pb-5">
                <div>
                  <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Reasoning</label>
                  <p className="text-sm text-gray-700 dark:text-gray-200">{selectedEmailForDetails.aiAgentReasoning}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Action</label>
                  <p className="text-sm text-gray-700 dark:text-gray-200">{selectedEmailForDetails.aiAgentAction}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Generated Response</label>
                  <p className="text-sm text-gray-700 dark:text-gray-200 whitespace-pre-wrap">{selectedEmailForDetails.aiAgentResponse}</p>
                </div>



                <div>
                  <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Processed At</label>
                  <p className="text-sm text-gray-700 dark:text-gray-200">
                    {selectedEmailForDetails.aiProcessedAt?.toDate
                      ? selectedEmailForDetails.aiProcessedAt.toDate().toLocaleString()
                      : "Unknown"}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Large Screen - Centered Modal */}
          <div style={{ margin: 0 }} className="hidden sm:flex fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 items-center justify-center" onClick={() => setShowAgentDetails(false)}>
            <div className="relative mx-auto p-5 border dark:border-gray-700 max-w-lg w-full shadow-lg rounded-md bg-white dark:bg-gray-800" onClick={(e) => e.stopPropagation()}>
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-700 dark:text-gray-200">AI Agent Details</h3>
                <button onClick={() => setShowAgentDetails(false)} className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300">
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="space-y-4 max-h-96 overflow-y-auto">
                <div>
                  <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Reasoning</label>
                  <p className="text-sm text-gray-700 dark:text-gray-200">{selectedEmailForDetails.aiAgentReasoning}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Action</label>
                  <p className="text-sm text-gray-700 dark:text-gray-200">{selectedEmailForDetails.aiAgentAction}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Generated Response</label>
                  <p className="text-sm text-gray-700 dark:text-gray-200 whitespace-pre-wrap">{selectedEmailForDetails.aiAgentResponse}</p>
                </div>


                <div>
                  <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Processed At</label>
                  <p className="text-sm text-gray-700 dark:text-gray-200">
                    {selectedEmailForDetails.aiProcessedAt?.toDate
                      ? selectedEmailForDetails.aiProcessedAt.toDate().toLocaleString()
                      : "Unknown"}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
